import React, { useState, useEffect, useRef, useCallback } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "./supabaseClient";
import Auth from "./Auth";
import Statistics from "./statistics";
import Settings from "./Settings";
import LoadingMessage from "./LoadingMessage";
import LogDetail from "./LogDetail";
import ResetPassword from "./ResetPassword";
import ForgotPassword from "./ForgotPassword";
import "./App.css";
import TypeWriterEffect from "react-typewriter-effect";
import BottomNav from "./BottomNav";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts"; 
import LiquidEtherWrapper from "./LiquidEtherWrapper";
import i18n from "./i18n";
import { useTranslation } from "react-i18next";
import LightPillar from "./LightPillar";
import InfoTooltip from "./InfoTooltip";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import CzDialog from "./CzDialog2";


const App = () => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [result, setResult] = useState("");
  const [carbohydrates, setCarbohydrates] = useState("");
  const [proteins, setProteins] = useState("");
  const [fats, setFats] = useState("");
  const [calories, setCalories] = useState("");
  const [bloodSugar, setBloodSugar] = useState("");
  const [insulinUnits, setInsulinUnits] = useState("");
  const [loading, setLoading] = useState(false);
  const [icRatio, setIcRatio] = useState("");
  const [fsi, setFsi] = useState("");
  const [targetGlycemia, setTargetGlycemia] = useState("");
  const [note, setNote] = useState("");
  const [currentTime, setCurrentTime] = useState("");
  const [timezone, setTimezone] = useState("Europe/Rome");
  const [step, setStep] = useState(1);
  const [lastLogId, setLastLogId] = useState(null);
  const [kowalskaInsulin, setKowalskaInsulin] = useState(null);
  const [kowalskaEquivalent, setKowalskaEquivalent] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [language, setLanguage] = useState("it");
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [charmDetected, setCharmDetected] = useState(false);
  const [coinDetected, setCoinDetected] = useState(false);

  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [startingCamera, setStartingCamera] = useState(false);
  const cameraStartingRef = useRef(false);

  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    message: "",
    variant: "info",
  });

  const showDialog = (message, opts = {}) => {
    setDialog({
      open: true,
      title: opts.title || "",
      message,
      variant: opts.variant || "info",
    });
  };

  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));

  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();

  const liquidColors = ['#ffcd27ff', '#ffd24d', '#ecd718'];

  const [analysisStatus, setAnalysisStatus] = useState("idle"); 
  // "idle" | "running" | "done" | "error"

  const analysisAbortRef = useRef(null);
  const analysisReqIdRef = useRef(0);

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language]);  
  useEffect(() => {
    const savedLang = localStorage.getItem("appLanguage");
    if (savedLang) {
      setLanguage(savedLang);
      i18n.changeLanguage(savedLang);
    }
  }, []);
  useEffect(() => {
    localStorage.setItem("appLanguage", language);
  }, [language]);

  useEffect(() => {
    const getSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      setSession(session);
      if (session) {
        await fetchProfile(session.user.id);
        await fetchTimezone(session.user.id);
      }
    };
    getSession();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        fetchProfile(session.user.id);
        fetchTimezone(session.user.id);
    } else {
      try { analysisAbortRef.current?.abort(); } catch {}
      analysisReqIdRef.current += 1;
      setAnalysisStatus("idle");
      setLoading(false);

      setProfile(null);
      stopEmbeddedCamera();
      setPhoto(null);
    }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (step === 3 && loading) {
      document.body.classList.add("analysis-bg");
    } else {
      document.body.classList.remove("analysis-bg");
    }
  }, [step, loading]);
  
  const fetchTimezone = async (userId) => {
    const { data, error } = await supabase
      .from("users")
      .select("timezone")
      .eq("id", userId)
      .single();
    if (!error && data?.timezone) {
      setTimezone(data.timezone);
    }
  };

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      try {
        const options = {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
          timeZone: timezone,
        };
        setCurrentTime(new Intl.DateTimeFormat("en-GB", options).format(now));
      } catch (e) {
        console.error("Errore nel calcolo orario con fuso:", e);
        setCurrentTime(
          now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        );
      }
    };
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, [timezone]);

  useEffect(() => {
    if (dialog.open) {
      setDialog(d => ({ ...d, open: false }));
    }
  }, [step, location.pathname]);


  useEffect(() => {
    if (!isNativeCapacitor()) return;

    let remove = null;

    (async () => {
      try {
        const mod = await import("@capacitor/local-notifications");
        const { LocalNotifications } = mod;

        const handler = (event) => {
          const extra = event?.notification?.extra || event?.notification?.data || {};
          const logId = extra?.logId;
          if (logId) {
            navigate(`/log/${logId}`);
          } else {
            navigate("/stats");
          }
        };

        // Capacitor ritorna un handle con remove()
        const listener = await LocalNotifications.addListener(
          "localNotificationActionPerformed",
          handler
        );

        remove = () => listener.remove();
      } catch (e) {
        console.error("Notifiche: errore listener", e);
      }
    })();

    return () => {
      try {
        remove?.();
      } catch {}
    };
  }, [navigate]);


  const fetchProfile = async (userId) => {
    const { data, error } = await supabase
      .from("users")
      .select(
        "ic_mattino, ic_pomeriggio, ic_sera, fsi, glicemia_target, timezone, notif_bolo, notif_valutazione"
      )
      .eq("id", userId)
      .single();

    if (!error && data) {
      setProfile(data);
      setFsi(data.fsi || "");
      setTargetGlycemia(data.glicemia_target || "");

      const now = new Date();
      const userTime = new Date(
        now.toLocaleString("en-US", { timeZone: data.timezone || "Europe/Rome" })
      );
      const hour = userTime.getHours();

      let ic;
      if (hour >= 6 && hour < 10) ic = data.ic_mattino;
      else if (hour >= 10 && hour < 16) ic = data.ic_pomeriggio;
      else if (hour >= 16 && hour < 22) ic = data.ic_sera;
      else ic = data.ic_pomeriggio;
      setIcRatio(ic || "");
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "#FFD24D",
            padding: "8px 12px",
            borderRadius: "10px",
            border: "1px solid #3390B3",
            color: "#3390B3",
            fontWeight: 600,
            fontSize: "0.9rem",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {payload[0].name}: {payload[0].value} g
        </div>
      );
    }
    return null;
  };

  const resizeImage = (base64Str, maxWidth = 800) =>
    new Promise((resolve) => {
      const img = new Image();
      img.src = base64Str;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scaleSize = maxWidth / img.width;
        canvas.width = maxWidth;
        canvas.height = img.height * scaleSize;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      };
    });

  const isNativeCapacitor = () => {
    return !!(window?.Capacitor?.isNativePlatform?.() || window?.Capacitor?.isNative);
  };

  // --- 🔔 NOTIFICHE (solo Capacitor) ---
  const ensureNotifPermission = async () => {
    if (!isNativeCapacitor()) return false;

    try {
      const mod = await import("@capacitor/local-notifications");
      const { LocalNotifications } = mod;

      const perm = await LocalNotifications.checkPermissions();
      if (perm.display === "granted") return true;

      const req = await LocalNotifications.requestPermissions();
      return req.display === "granted";
    } catch (e) {
      console.error("Notifiche: errore permessi", e);
      return false;
    }
  };

  // id numerico stabile per evitare doppioni (logId serial -> ok)
  const notifIdFor = (logId, type) => Number(logId) * 10 + Number(type); // type: 1 bolo, 2 review

  const scheduleNotificationsAfterAnalysis = async ({ logId, baseDate, prefs, ugpInsulin}) => {
    if (!isNativeCapacitor()) return;
    if (!logId) return;

    const wantBolo = !!prefs?.notif_bolo;
    const wantReview = !!prefs?.notif_valutazione;
    if (!wantBolo && !wantReview) return;

    const ugpNum =
      ugpInsulin === null || ugpInsulin === undefined || ugpInsulin === ""
        ? 0
        : Number(ugpInsulin);

    const hasRealUgp = Number.isFinite(ugpNum) && ugpNum > 0;

    if (wantBolo && !hasRealUgp && !wantReview) return;

    const granted = await ensureNotifPermission();
    if (!granted) return;

    try {
      const mod = await import("@capacitor/local-notifications");
      const { LocalNotifications } = mod;

      // tempo base = creazione log (fine analisi)
      const base = baseDate instanceof Date ? baseDate : new Date();

      const notifications = [];

      if (wantBolo && hasRealUgp) {
        const when = new Date(base.getTime() + 110 * 60 * 1000);
        notifications.push({
          id: notifIdFor(logId, 1),
          title: "🟡 CarboZen – Bolo ritardato (UGP)",
          body: "Mantieni la tua glicemia in range.",
          schedule: { at: when, allowWhileIdle: true },
          smallIcon: "ic_stat_carbozen",
          largeIcon: "ic_carbozen_large",
          extra: { logId: String(logId), type: "bolo" },
        });
      }

      if (wantReview) {
        const when = new Date(base.getTime() + 4 * 60 * 60 * 1000);
        notifications.push({
          id: notifIdFor(logId, 2),
          title: "⭐ CarboZen – Com’è andata?",
          body: "Raccontaci come sei stato dopo il pasto.",
          schedule: { at: when, allowWhileIdle: true },
          smallIcon: "ic_stat_carbozen",
          largeIcon: "ic_carbozen_large",
          extra: { logId: String(logId), type: "review" },
        });
      }
       
      if (notifications.length === 0) return;
      // opzionale: prima cancelliamo eventuali notifiche con stessi id (se l’utente rifà sync/bug)
      await LocalNotifications.cancel({ notifications: notifications.map((n) => ({ id: n.id })) });

      await LocalNotifications.schedule({ notifications });
    } catch (e) {
      console.error("Notifiche: errore schedule", e);
    }
  };

  const ValueBox = ({ value, unit }) => (
    <div
      className="glicemia-input2"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "baseline",
        gap: 6,
        width: "100%",
      }}
    >
      <span style={{ fontWeight: 800, fontSize: "1.25rem" }}>
        {value ?? "-"}
      </span>
      {unit ? (
        <span style={{ fontSize: "0.95rem", fontWeight: 700, opacity: 0.9 }}>
          {unit}
        </span>
      ) : null}
    </div>
  );


  const stopEmbeddedCamera = useCallback(() => {
    try {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }
    } catch (e) {}

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    setCameraReady(false);
    setStartingCamera(false);
  }, []);

  const startEmbeddedCamera = useCallback(async () => {
    if (cameraStartingRef.current) return;
    cameraStartingRef.current = true;

    setCameraError("");
    setStartingCamera(true);

    if (step !== 1 || photo) {
      cameraStartingRef.current = false;
      setStartingCamera(false);
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("Camera non supportata su questo browser.");
      cameraStartingRef.current = false;
      setStartingCamera(false);
      return;
    }

    try {
      // Se esiste già uno stream attivo, non riaprire
      if (streamRef.current) {
        const video = videoRef.current;
        if (video) {
          try {
            if (video.srcObject !== streamRef.current) {
              video.srcObject = streamRef.current;
            }
            video.setAttribute("playsinline", "true");
            video.setAttribute("webkit-playsinline", "true");

            // aspetta metadata (alcuni browser lo richiedono anche qui)
            await new Promise((resolve) => {
              const handler = () => {
                video.removeEventListener("loadedmetadata", handler);
                resolve();
              };
              video.addEventListener("loadedmetadata", handler);
              if (video.readyState >= 1) {
                video.removeEventListener("loadedmetadata", handler);
                resolve();
              }
            });

            await video.play();
            setCameraReady(true);
          } catch (e) {
            // se fallisce, meglio chiudere tutto e riaprire da zero
            stopEmbeddedCamera();
          }
        }

        cameraStartingRef.current = false;
        setStartingCamera(false);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });

      streamRef.current = stream;

      const video = videoRef.current;
      if (!video) {
        throw new Error("videoRef non disponibile");
      }

      video.srcObject = stream;
      video.setAttribute("playsinline", "true");
      video.setAttribute("webkit-playsinline", "true");

      // aspetta metadata prima di play (riduce AbortError)
      await new Promise((resolve) => {
        const handler = () => {
          video.removeEventListener("loadedmetadata", handler);
          resolve();
        };
        video.addEventListener("loadedmetadata", handler);
        if (video.readyState >= 1) {
          video.removeEventListener("loadedmetadata", handler);
          resolve();
        }
      });

      await video.play();
      setCameraReady(true);
    } catch (err) {
      console.error("Camera embedded error:", err);
      setCameraError("Permesso fotocamera negato o non disponibile.");
      setCameraReady(false);

      // IMPORTANTISSIMO: se fallisce, stoppa stream (evita loop)
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    } finally {
      cameraStartingRef.current = false;
      setStartingCamera(false);
    }
  }, [step, photo, stopEmbeddedCamera]);


  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = async () => {
        const resizedBase64 = await resizeImage(reader.result);
        setPhoto(resizedBase64);
      };
      reader.readAsDataURL(file);
    }
  };

  const captureFromEmbeddedCamera = async () => {
    if (!videoRef.current) return;

    const video = videoRef.current;

    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);

    const base64 = canvas.toDataURL("image/jpeg", 0.85);
    const resizedBase64 = await resizeImage(base64);

    setPhoto(resizedBase64);
    setPhotoFile(null);

    stopEmbeddedCamera();
  };

  const captureFromNativeCamera = async () => {
    try {
      if (!isNativeCapacitor()) return;

      // import dinamico: su web non viene caricato nulla
      const mod = await import("@capacitor/camera");
      const { Camera, CameraResultType, CameraSource } = mod;

      const shot = await Camera.getPhoto({
        quality: 85,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        saveToGallery: false,
      });

      if (shot?.dataUrl) {
        const resizedBase64 = await resizeImage(shot.dataUrl);
        setPhoto(resizedBase64);
        setPhotoFile(null);
        stopEmbeddedCamera();
      }
    } catch (err) {
      console.error("Native camera error:", err);
      showDialog(t("cameraNativeFail") || "Impossibile aprire la fotocamera nativa. Usa 'Scegli immagine'.", {
        title: "Fotocamera",
        variant: "warning",
      });      
    }
  };

  useEffect(() => {
    if (!session) {
      stopEmbeddedCamera();
      return;
    }

    // ✅ Se non sei nella home, spegni sempre la camera
    if (location.pathname !== "/") {
      stopEmbeddedCamera();
      return;
    }

    // ✅ Sei nella home
    if (step === 1 && !photo) {
      startEmbeddedCamera();
    } else {
      stopEmbeddedCamera();
    }

    return () => stopEmbeddedCamera();
  }, [session, location.pathname, step, photo, startEmbeddedCamera, stopEmbeddedCamera]);

  const runAnalysis = async ({ forceUI = false } = {}) => {
    if (!photo) return;

    const safeDescription = description.trim()
      ? description
      : "Stima i valori nutrizionali (carboidrati, proteine, grassi, calorie) di questo piatto.";

    // evita doppioni
    if (analysisStatus === "running") return;
    if (analysisStatus === "done" && result) return;

    // cancella eventuale richiesta precedente
    try { analysisAbortRef.current?.abort(); } catch {}
    const controller = new AbortController();
    analysisAbortRef.current = controller;

    const reqId = ++analysisReqIdRef.current;

    setAnalysisStatus("running");

    if (forceUI) {
      setStep(3);
      setLoading(true);
    }

    // reset risultati (soft)
    setResult("");
    setCarbohydrates("");
    setProteins("");
    setFats("");
    setCalories("");
    setInsulinUnits("");
    setCharmDetected(false);
    setCoinDetected(false);

    try {
      const fileName = `photo_${Date.now()}.jpg`;
      const base64Data = photo.split(",")[1];

      const file = new File(
        [Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0))],
        fileName,
        { type: "image/jpeg" }
      );

      const { error: uploadError } = await supabase.storage
        .from("mealphotos")
        .upload(fileName, file);

      if (uploadError) throw new Error("Errore upload immagine su Supabase");

      const { data: publicUrlData } = supabase.storage
        .from("mealphotos")
        .getPublicUrl(fileName);

      // se nel frattempo è partita un'altra analisi, ignora
      if (analysisReqIdRef.current !== reqId) return;

      setImageUrl(publicUrlData.publicUrl);

      console.log(`[analyze#${reqId}] -> POST /analyze`, {
        language: (language || i18n.language || "it"),
        hasPhoto: !!photo,
        imageUrl: publicUrlData.publicUrl,
      });

      const response = await fetch("https://carbozen.onrender.com/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          image: publicUrlData.publicUrl,
          description: safeDescription,
          language: language || i18n.language || "it",
          user_id: session?.user?.id || null,
        }),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(`Analyze failed ${response.status}: ${text.slice(0, 200)}`);
      }

      const data = await response.json();

      console.log(`[analyze#${reqId}] <- response`, {
        ok: response.ok,
        status: response.status,
        keys: Object.keys(data || {}),
        charmDetected: data?.charmDetected,
        coinDetected: data?.coinDetected,
        nutrients: data?.nutrients ? "yes" : "no",
      });

      if (analysisReqIdRef.current !== reqId) return;

      const { nutrients, fullAnalysis } = data;

      setCharmDetected(String(data?.charmDetected || "").toUpperCase() === "SI");
      setCoinDetected(String(data?.coinDetected || "").toUpperCase() === "SI");

      setResult(fullAnalysis || "");

      if (nutrients) {
        setCarbohydrates(nutrients.carbohydrates?.toFixed(1) || "");
        setProteins(nutrients.proteins?.toFixed(1) || "");
        setFats(nutrients.fats?.toFixed(1) || "");
        setCalories(nutrients.calories?.toFixed(0) || "");
      }

      setAnalysisStatus("done");
      } catch (e) {
        if (e?.name === "AbortError") return; // abort = ok, non è un errore
        console.error(`[analyze#${reqId}] ERROR`, e);

        if (analysisReqIdRef.current === reqId) {
          setAnalysisStatus("error");
          setResult("Errore durante l'analisi.");
        }
      } finally {
      if (analysisReqIdRef.current === reqId) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // ✅ Se cambia foto, i risultati vecchi non sono più validi
    try { analysisAbortRef.current?.abort(); } catch {}
    analysisReqIdRef.current += 1;

    setAnalysisStatus("idle");
    setLoading(false);

    setResult("");
    setCarbohydrates("");
    setProteins("");
    setFats("");
    setCalories("");
    setInsulinUnits("");
    setCharmDetected(false);
    setCoinDetected(false);
    setImageUrl(null);
  }, [photo]);

  const roundHalf = (num) => {
    if (!Number.isFinite(num) || num <= 0) return 0;

    // sotto 0.25 UI → clinicamente 0
    if (num < 0.25) return 0;

    return Math.round(num * 2) / 2;
  };

  const calculateInsulin = async () => {
    if (!carbohydrates || !bloodSugar || !icRatio || !fsi || !targetGlycemia) {
      showDialog(t("alertMissingFields") || "Inserisci tutti i dati richiesti.", { variant: "warning" });
      return;
    }

    try {
      const carbs = parseFloat(carbohydrates);
      const bg = parseFloat(bloodSugar);
      const ic = parseFloat(icRatio);
      const f = parseFloat(fsi);
      const t = parseFloat(targetGlycemia);

      const mealInsulin = carbs / ic;
      const correctionInsulin = (bg - t) / f;
      const total = Math.max(mealInsulin + correctionInsulin, 0);
      const rounded = Math.round(total * 10) / 10;
      setInsulinUnits(rounded);

      const kowalskaRes = await fetch("https://carbozen.onrender.com/calculate-kowalska", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          proteins: parseFloat(proteins),
          fats: parseFloat(fats),
        }),
      });

      const kowalskaData = await kowalskaRes.json();
      setKowalskaInsulin(parseFloat(kowalskaData.additionalBolus)); // 💉 solo il bolo extra
      setKowalskaEquivalent(null);

      let finalImageUrl = imageUrl;

      const { data: insertData, error } = await supabase
        .from("insulin_logs")
        .insert({
          user_id: session.user.id,
          glicemia: bg,
          carboidrati: carbs,
          proteine: proteins,
          grassi: fats,
          calorie: calories,
          insulina_calcolata: rounded,
          insulina_kowalska: kowalskaData.additionalBolus,
          cho_equivalenti_kowalska: null,
          image_url: finalImageUrl,
          risultato_ai: result,
          descrizione_inserita: description,
          charm_detected: charmDetected,
          coin_detected: coinDetected,
        })
        .select()
        .single();

      if (!error && insertData?.id) setLastLogId(insertData.id);

     // 🔔 SCHEDULA NOTIFICHE 
      if (!error && insertData?.id) {
        await scheduleNotificationsAfterAnalysis({
          logId: insertData.id,
          baseDate: new Date(), // ✅ momento reale sul device
          prefs: {
            notif_bolo: profile?.notif_bolo,
            notif_valutazione: profile?.notif_valutazione,
          },
          ugpInsulin: kowalskaData.additionalBolus,
        });
      }


      setStep(4); 
    } catch (error) {
      console.error("Errore durante il calcolo:", error);
      setInsulinUnits("Errore durante il calcolo.");
    }
  };


  const resetWizard = async () => {
    try { analysisAbortRef.current?.abort(); } catch {}
    analysisReqIdRef.current += 1;   // ✅ invalida eventuali risposte in arrivo
    setAnalysisStatus("idle");
    setLoading(false);  
    if (lastLogId && note.trim()) {
      try {
        await supabase
          .from("insulin_logs")
          .update({ note: note })
          .eq("id", lastLogId);
      } catch (error) {
        console.error("Errore durante il salvataggio della nota:", error);
      }
    }

    setStep(1);
    setDescription("");
    setPhoto(null);
    setPhotoFile(null);
    setResult("");
    setCarbohydrates("");
    setProteins("");
    setFats("");
    setCalories("");
    setBloodSugar("");
    setInsulinUnits("");
    setNote("");
    setLastLogId(null);
    setKowalskaInsulin(null);
    setKowalskaEquivalent(null);
    setImageUrl(null);
    setCharmDetected(false);
    setCoinDetected(false);
  };

  const pieData = [
    { name: "Carboidrati", value: parseFloat(carbohydrates) || 0 },
    { name: "Proteine", value: parseFloat(proteins) || 0 },
    { name: "Grassi", value: parseFloat(fats) || 0 },
  ];

  const COLORS = ["#3390B3", "#2ECC71", "#E67E22"];

  if (!session) {
    return (
      <Routes>
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="*"
          element={<Auth onLogin={(user) => setSession({ user })} />}
        />
      </Routes>
    );
  }

  return (
  <div className="app-root">
    <main className="app-content">
      <div className="app-layout">
        <CzDialog open={dialog.open} title={dialog.title} message={dialog.message} variant={dialog.variant} confirmText="OK" onConfirm={closeDialog} />
          {step === 3 && loading && (
          <div className="color-bends-bg-layer">
            <LightPillar
              topColor="#ffd24d"
              bottomColor="#ffd24d"
              intensity={1.0}
              rotationSpeed={1.0}
              glowAmount={0.001}
              pillarWidth={6.0}
              pillarHeight={1.1}
              noiseIntensity={0.1}
              pillarRotation={135}
              interactive={false}
              mixBlendMode="normal"
            />
          </div>
        )}
      <Routes>
        <Route
          path="/"
          element={
            <div className="container">
            <a href="https://carbozen.it">
              <img
                src={step === 3 && loading ? "/logo-yellow.png" : "/logo.png"}
                alt="CarboZen Logo"
                className="logo-image"
              />
            </a>
              <p className="instructions">
                {t("fraseSottoLogo")}
              </p>
              {/* 🔔 Banner novità 2026 (ultra-compact) */}
              <div
                className="stats-inset clickable cz-banner"
                role="button"
                tabIndex={0}
                onClick={() =>
                  showDialog(
                    t("news2026DialogText"),
                    { title: t("news2026Title"), variant: "info" }
                  )
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    showDialog(
                      t("news2026DialogText"),
                      { title: t("news2026Title"), variant: "info" }
                    );
                  }
                }}
                title={t("news2026Title")}
              >
                <span className="cz-banner-badge">
                  {t("news2026BannerPrefix")}
                </span>{" "}
                <span className="cz-banner-text">
                  {t("news2026BannerText")}
                </span>
              </div>
              <div
                className={`wizard-card wizard-card-home ${step === 3 && loading ? "analysis-card" : ""}`}
                style={{ position: "relative" }}
              >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  zIndex: 2,
                  opacity: step === 3 && loading ? 1 : 0,
                  pointerEvents: step === 3 && loading ? "auto" : "none",
                  transition: "opacity 0.6s ease",
                }}
              >
                <LiquidEtherWrapper
                  colors={liquidColors}
                  mouseForce={20}
                  pointerEvents="auto"
                  cursorSize={60}
                  isViscous={false}
                  viscous={30}
                  iterationsViscous={32}
                  iterationsPoisson={32}
                  resolution={0.5}
                  isBounce={false}
                  autoDemo={true}
                  autoSpeed={0.25}
                  autoIntensity={1.5}
                  takeoverDuration={0.25}
                  autoResumeDelay={100}
                  autoRampDuration={0.6}
                  className="liquid-ether-wrapper"
                  style={{ width: "100%", height: "100%" }}
                />
              </div>
              

                <div className="wizard-card-content">
                  {step === 1 && (
                    <div>
                      {!photo ? (
                      <>
                        {/* 📷 Camera responsive: grande su tutti gli schermi */}
                        <div
                          style={{
                            width: "100%",
                            borderRadius: 16,
                            overflow: "hidden",
                            background: "rgba(255,255,255,0.35)",
                            border: "1px solid rgba(51,144,179,0.25)",
                            boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                            marginTop: 14,
                            marginBottom: 12,
                            position: "relative",
                          }}
                        >
                          <div
                            style={{
                              width: "100%",
                              // ✅ altezza adattiva: mobile grande, desktop equilibrata
                              height: "min(52svh, 520px)",
                              // ✅ evita che diventi troppo bassa su schermi piccoli
                              minHeight: 240,
                            }}
                          >
                          {/* 🎯 Shutter dentro al riquadro */}
                          {cameraReady && (
                            <div
                              style={{
                                position: "absolute",
                                left: "50%",
                                bottom: 14,
                                transform: "translateX(-50%)",
                                zIndex: 5,
                                display: "flex",
                                justifyContent: "center",
                                width: "100%",
                                pointerEvents: "none",
                              }}
                            >
                              <div style={{ pointerEvents: "auto" }}>
                                <button
                                  type="button"
                                  onClick={captureFromEmbeddedCamera}
                                  aria-label={t("scattaFoto")}
                                  style={{
                                    width: 78,
                                    height: 78,
                                    borderRadius: 999,
                                    border: "2px solid rgba(255,255,255,0.95)",
                                    background: "rgba(255,255,255,0.12)",
                                    backdropFilter: "blur(8px)",
                                    WebkitBackdropFilter: "blur(8px)",
                                    boxShadow:
                                      "0 10px 24px rgba(0,0,0,0.22), 0 0 0 4px rgba(51,144,179,0.18)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    touchAction: "manipulation",
                                    WebkitTapHighlightColor: "transparent",
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 56,
                                      height: 56,
                                      borderRadius: 999,
                                      background: "rgba(255,255,255,0.90)",
                                      boxShadow:
                                        "inset 0 0 0 2px rgba(51,144,179,0.22), 0 8px 18px rgba(0,0,0,0.12)",
                                      display: "block",
                                    }}
                                  />
                                </button>
                              </div>
                            </div>
                          )}
                            <video
                              ref={videoRef}
                              muted
                              playsInline
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: cameraReady ? "block" : "none",
                              }}
                            />

                            {!cameraReady && (
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: 14,
                                  textAlign: "center",
                                }}
                              >
                                <p className="instructions" style={{ margin: 0 }}>
                                  {cameraError
                                    ? cameraError
                                    : startingCamera
                                    ? "Apro la fotocamera…"
                                    : "Fotocamera pronta all’avvio."}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Bottoni: sempre sotto, in ordine chiaro */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

                          {isNativeCapacitor() && !!cameraError && (
                            <button className="custom-button4" onClick={captureFromNativeCamera}>
                              {t("apriFotocamera")}
                            </button>
                          )}
                          <label className="custom-button4 upload-button" style={{ marginTop: 2 }}>
                            {t("caricaImmagine")}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageUpload}
                              className="file-input-hidden"
                            />
                          </label>
                        </div>
                      </>
                      ) : (
                        <>
                          <img src={photo} alt="Anteprima" className="preview-image" />

                          {/* textarea “nuovo stile”: stessa filosofia di glicemia-input2 / form-input */}
                          <textarea
                            className="form-input cz-textarea"
                            placeholder={t("descrizione")}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                          />
                        </>
                      )}
                    </div>
                  )}


                  {step === 2 && (
                    <div>
                      <h1 className="titlesec">{t("inserisciParametri")}</h1>

                      {/* 🩸 Glicemia attuale (con pulse come LogDetail) */}
                      <p className="form-label" style={{ marginBottom: 6, textAlign: "center" }}>
                        <span className="label-with-tooltip">
                          {t("glicemiaAttuale")} <InfoTooltip text={t("infoGlicemiaAttuale")} />
                        </span>
                      </p>

                      <div
                        className={!bloodSugar ? "pulse-wrap" : ""}
                        style={{ borderRadius: 12, display: "flex", justifyContent: "center" }}
                      >
                        <input
                          type="number"
                          placeholder="mg/dL"
                          value={bloodSugar}
                          onChange={(e) => setBloodSugar(e.target.value)}
                          onBlur={() => {
                            if (bloodSugar && parseFloat(bloodSugar) < 70) {
                              showDialog(t("alertGlicemia"), { title: t("attenzione") || "Attenzione", variant: "warning" });
                            }
                          }}
                          className="glicemia-input2"
                          style={{ maxWidth: 220, textAlign: "center", fontWeight: 700 }}
                        />
                      </div>

                      {/* I/C */}
                      <p className="form-label" style={{ marginTop: 14, marginBottom: 6, textAlign: "center" }}>
                        I/C
                      </p>
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <input
                          type="number"
                          value={icRatio}
                          onChange={(e) => setIcRatio(e.target.value)}
                          className="glicemia-input2"
                          style={{ maxWidth: 220, textAlign: "center", fontWeight: 700 }}
                        />
                      </div>

                      {/* FSI */}
                      <p className="form-label" style={{ marginTop: 14, marginBottom: 6, textAlign: "center" }}>
                        {t("fsi")}
                      </p>
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <input
                          type="number"
                          value={fsi}
                          onChange={(e) => setFsi(e.target.value)}
                          className="glicemia-input2"
                          style={{ maxWidth: 220, textAlign: "center", fontWeight: 700 }}
                        />
                      </div>

                      {/* Target */}
                      <p className="form-label" style={{ marginTop: 14, marginBottom: 6, textAlign: "center" }}>
                        {t("glicemiaTarget")}
                      </p>
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <input
                          type="number"
                          value={targetGlycemia}
                          onChange={(e) => setTargetGlycemia(e.target.value)}
                          className="glicemia-input2"
                          style={{ maxWidth: 220, textAlign: "center", fontWeight: 700 }}
                        />
                      </div>
                    </div>
                  )}


                  {step === 3 && (
                    <div>
                      {!loading && result && (charmDetected || coinDetected) && (
                        <div
                          style={{
                            marginTop: 10,
                            marginBottom: 10,
                            display: "flex",
                            justifyContent: "center",
                            gap: 10,
                            flexWrap: "wrap",
                          }}
                        >
                        {charmDetected && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                              fontWeight: 700,
                              color: "#3390B3",
                              fontSize: 13,
                              background: "transparent",  
                              border: "none",           
                              padding: 0,              
                            }}
                          >
                            <img
                              src="/icona.png"
                              alt=""
                              style={{
                                width: 24,     
                                height: 24,    
                                flexShrink: 0,
                                display: "block",
                              }}
                            />
                            {t("charmDetected")}
                          </span>
                        )}

                        {coinDetected && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                              fontWeight: 700,
                              color: "#3390B3",
                              fontSize: 13,
                              background: "transparent",  
                              border: "none",
                              padding: 0,
                            }}
                          >
                            <img
                              src="/icona.png"
                              alt=""
                              style={{
                                width: 24,     
                                height: 24,    
                                flexShrink: 0,
                                display: "block",
                              }}
                            />
                            {t("coinDetected")}
                          </span>
                        )}

                        </div>
                      )}


                      {loading && (
                        <div className="analysis-focus">
                          <div className="analysis-spinner" />

                          <div style={{ marginTop: 6 }}>
                            <LoadingMessage showSpinner={false} />
                          </div>
                        </div>
                      )}

                      {!loading && result && (
                        <>
                      <p className="instructions" style={{ marginBottom: 8 }}>
                        <b>{t("risultato")}</b>
                      </p>

                      <div className="result-description">
                        <TypeWriterEffect
                          textStyle={{
                            fontSize: "inherit",
                            color: "inherit",
                            textAlign: "center",
                            fontWeight: 400,     
                            lineHeight: "1.45",
                          }}
                          startDelay={100}
                          cursorColor="#3390B3"
                          text={result}
                          typeSpeed={20}
                        />
                      </div>

                          {/* ✅ STESSO IDENTICO LAYOUT DI LogDetail: 2 righe x 2 colonne */}
                          {carbohydrates && proteins && fats && calories && (
                            <div className="settings-row-2" style={{ marginTop: 12 }}>
                              <div>
                                <div className="form-label settings-mini-label center">
                                  {t("carboidrati")}
                                </div>
                                <div className="input-wrapper input-wrapper-md">
                                  <ValueBox value={carbohydrates ?? "-"} unit="g" />
                                </div>
                              </div>

                              <div>
                                <div className="form-label settings-mini-label center">
                                  {t("proteine")}
                                </div>
                                <div className="input-wrapper input-wrapper-md">
                                  <ValueBox value={proteins ?? "-"} unit="g" />
                                </div>
                              </div>

                              <div>
                                <div className="form-label settings-mini-label center">
                                  {t("grassi")}
                                </div>
                                <div className="input-wrapper input-wrapper-md">
                                  <ValueBox value={fats ?? "-"} unit="g" />
                                </div>
                              </div>

                              <div>
                                <div className="form-label settings-mini-label center">
                                  {t("calorie")}
                                </div>
                                <div className="input-wrapper input-wrapper-md">
                                  <ValueBox value={calories ?? "-"} unit="kcal" />
                                </div>
                              </div>
                            </div>
                          )}


                          {carbohydrates && proteins && fats && (
                            <div
                              className="chart-container"
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flexDirection: "column",
                                width: "100%",
                                marginTop: "1rem",
                              }}
                            >
                              <h2 style={{ textAlign: "center", color: "#3390B3" }}>
                                {t("distribuzioneMacronutrienti")}
                              </h2>
                              <PieChart width={260} height={260}>
                                <Pie
                                  data={[
                                    { name: t("carboidrati"), value: parseFloat(carbohydrates) },
                                    { name: t("proteine"), value: parseFloat(proteins) },
                                    { name: t("grassi"), value: parseFloat(fats) },
                                  ]}
                                  dataKey="value"
                                  nameKey="name"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={90}
                                  label={false}        
                                  labelLine={false}
                                >
                                  <Cell fill="#3390B3" />
                                  <Cell fill="#33B37A" />
                                  <Cell fill="#FF7F50" />
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                                <Legend verticalAlign="bottom" />
                              </PieChart>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {step === 4 && (
                    <div>
                      {/* ✅ tolto titolo "Unità di insulina" */}

                      {insulinUnits && (
                        <>
                          <div className="insulin-results">
                            {/* ====== BOLO STANDARD ====== */}
                            <div className="result-card result-card-inset result-card-inset--accent">
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 8,
                                }}
                              >
                                <h3 style={{ margin: 0 }}>{t("calcoloStandard")}</h3>
                                <InfoTooltip text={t("boloDaAssumere")} />
                              </div>

                              {/* ✅ solo "8 UI" */}
                              <p style={{ marginTop: 10, marginBottom: 8, textAlign: "center" }}>
                                <span
                                  style={{
                                    fontSize: "2.0rem",
                                    fontWeight: 800,
                                    color: "#3390B3",
                                    letterSpacing: 0.3,
                                  }}
                                >
                                  {roundHalf(insulinUnits)}{" "}
                                  <span style={{ fontSize: "1.1rem", fontWeight: 700 }}>UI</span>
                                </span>
                              </p>

                              <p style={{ textAlign: "center", margin: 0 }}>
                                {t("basatoSu")} <b>{carbohydrates} g</b> {t("diCarboidrati")}
                              </p>

                              {/* ✅ tolta la frase lunga sotto (ora è nel tooltip) */}
                            </div>

                            {/* ====== BOLO UGP ====== */}
                            {kowalskaInsulin && (
                              <div className="result-card result-card-inset result-card-inset--accent" style={{ marginTop: "1.5rem" }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 8,
                                  }}
                                >
                                  <h3 style={{ margin: 0 }}>{t("formulaModificata")}</h3>
                                  <InfoTooltip text={t("ancheProteine")} />
                                </div>

                                {/* ✅ solo "X UI" */}
                                <p style={{ marginTop: 10, marginBottom: 8, textAlign: "center" }}>
                                  <span
                                    style={{
                                      fontSize: "2.0rem",
                                      fontWeight: 800,
                                      color: "#3390B3",
                                      letterSpacing: 0.3,
                                    }}
                                  >
                                    {roundHalf(kowalskaInsulin)}{" "}
                                    <span style={{ fontSize: "1.1rem", fontWeight: 700 }}>UI</span>
                                  </span>
                                </p>

                                <p style={{ textAlign: "center", margin: 0 }}>
                                  {t("basatoSu")} <b>{proteins} g</b> {t("proteineE")} <b>{fats} g</b>{" "}
                                  {t("grassi2")}
                                  <br />
                                  ({(proteins * 4 + fats * 9).toFixed(0)} {t("kcal")})
                                </p>
                                {!profile?.notif_bolo && (
                                  <p className="ugp-note">
                                    <b>{t("notaBoloUgp").split(":")[0]}:</b>
                                    {t("notaBoloUgp").split(":")[1]}
                                  </p>
                                )}

                                {/* ✅ tolta la frase lunga sotto (ora è nel tooltip) */}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                </div>

                <div className="wizard-buttons" style={{ position: "relative", zIndex: 50 }}>
                  {step > 1 && step < 4 && (
                    <button
                      type="button"
                      className="icon-nav"
                      onClick={() => setStep(step - 1)}
                      aria-label={t("indietro")}
                      title={t("indietro")}
                    >
                      <FiChevronLeft size={34} />
                    </button>
                  )}
                  {step === 1 && photo && (
                    <div style={{ display: "flex", gap: 24, width: "100%", justifyContent: "center" }}>
                      <button
                        type="button"
                        className="icon-nav"
                        onClick={() => {
                          try { analysisAbortRef.current?.abort(); } catch {}
                          analysisReqIdRef.current += 1;   // ✅ invalida eventuali risposte in arrivo
                          setAnalysisStatus("idle");
                          setLoading(false);

                          setPhoto(null);
                          setPhotoFile(null);
                          setDescription("");
                          setCameraError("");
                        }}
                        aria-label={t("indietro")}
                        title={t("indietro")}
                      >
                        <FiChevronLeft size={34} />
                      </button>

                      <button
                        type="button"
                        className="icon-nav"
                        onClick={() => {
                          setStep(2);
                          runAnalysis({ forceUI: false }); 
                        }}
                        aria-label={t("avanti")}
                        title={t("avanti")}
                      >
                        <FiChevronRight size={34} />
                      </button>
                    </div>
                  )}
                  {step === 2 && (
                    <button
                      className="custom-button4"
                      onClick={() => {
                        if (!photo) {
                          showDialog(t("alertNoPhoto") || "Carica una foto del piatto.", { variant: "warning" });
                          return;
                        }
                        if (!bloodSugar.trim()) {
                          showDialog(t("alertNoGlycemia") || "Inserisci la glicemia attuale prima di continuare.", { variant: "warning" });
                          return;
                        }

                        setStep(3);

                        if (analysisStatus === "done" && result) {
                          setLoading(false);
                          return;
                        }

                        if (analysisStatus === "running") {
                          setLoading(true);
                          return;
                        }

                        runAnalysis({ forceUI: true });
                      }}
                    >
                      {t("analizza")}
                    </button>
                  )}
                  {step === 3 && !loading && result && (
                    <button className="custom-button4" onClick={calculateInsulin}>{t("calcolaInsulina")}</button>
                  )}
                  {step === 4 && (
                    <button className="custom-button4" onClick={resetWizard}>{t("concludiAnalisi")}</button>
                  )}
                </div>
              </div>

            </div>
          }
        />

      <Route path="/stats" element={<Statistics userId={session?.user?.id} />} />
      <Route
        path="/settings"
        element={<Settings userId={session?.user?.id} />}
      />
      <Route path="/log/:id" element={<LogDetail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
    </Routes>
      </div>
    </main>

    <BottomNav />
  </div>
);
};

export default App;
